// Chronological per-user activity table rendered on UserDetailPage.
// Merges sim and course events into a single sorted stream and renders
// them as a compact, scannable table (Date / Activity / Source).
//
// Calculations: filter SEND_LOG + COURSE_SEND_LOG by email, tag each
// event with its source type, sort by dateSent DESC.
//
// Postgres mapping: union of `simulations_sent` and `course_sends`
// filtered by user_id. Could become a unified `user_activity_v` view.
// Integration hook: `useUserActivityQuery(email, { limit })` cache 30 min.
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import MainCard from '../../../components/cards/MainCard';

const EVENT_META = {
    received:    { label: 'Received',         color: '#A0A8B8', faded: true },
    clicked:     { label: 'Clicked',          color: '#FFCA28' },
    clicked_2nd: { label: '2nd-stage Click',  color: '#FF5252' },
    reported:    { label: 'Reported',         color: '#66BB6A' },
    assigned:    { label: 'Course Assigned',  color: '#00BFA5' },
    completed:   { label: 'Course Completed', color: '#66BB6A' },
};

function fmtDay(s) { return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); }

function buildEvents(simEvents, courseEvents) {
    const out = [];
    for (const e of simEvents) {
        const kind = e.status === 'clicked' ? 'clicked'
            : e.status === 'clicked_2nd' ? 'clicked_2nd'
            : e.status === 'reported' ? 'reported'
            : 'received';
        out.push({
            kind,
            date: e.dateSent,
            entity: e.simName,
            entityKind: 'sim',
            source: e.source,
            meta: [e.device, e.location].filter(Boolean).join(' - '),
        });
    }
    for (const e of courseEvents) {
        out.push({ kind: 'assigned', date: e.dateSent, entity: e.courseName, entityKind: 'course', source: e.source, meta: e.courseType });
        if (e.dateCompleted) {
            out.push({ kind: 'completed', date: e.dateCompleted, entity: e.courseName, entityKind: 'course', source: e.source, meta: e.courseType });
        }
    }
    return out.sort((a, b) => new Date(b.date) - new Date(a.date));
}

// Visual identity per assignment source. Hue stays neutral but each source
// has a distinct accent so the eye can scan the column quickly.
const SOURCE_META = {
    'Journey Builder': { color: '#5B6CFF', dot: '#5B6CFF' },
    'On-Demand':       { color: '#00BFA5', dot: '#00BFA5' },
    'Auto Assign':     { color: '#8B5CF6', dot: '#8B5CF6' },
    'Content Pack':    { color: '#FFCA28', dot: '#FFCA28' },
    'Direct Send':     { color: '#6B7591', dot: '#6B7591' },
    // The platform has no assignment-channel telemetry, so hydrated events
    // carry 'Unknown'. Render a neutral badge (the dim-text grey) rather than
    // mislabelling it 'Direct Send' or dropping the element, so the row
    // layout stays identical to the prototype.
    'Unknown':         { color: '#A0A8B8', dot: '#A0A8B8' },
};

function SourceBadge({ source }) {
    if (!source) return null;
    const m = SOURCE_META[source] || SOURCE_META['Direct Send'];
    return (
        <Box
            title={`Assigned via ${source}`}
            sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                px: '6px', py: '1px', borderRadius: '4px',
                fontSize: 9, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
                color: m.color, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
                whiteSpace: 'nowrap',
            }}
        >
            <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: m.dot, flexShrink: 0 }} />
            {source}
        </Box>
    );
}

function KindLabel({ kind }) {
    const meta = EVENT_META[kind];
    return (
        <Box component="span" sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
            color: meta.color, whiteSpace: 'nowrap',
        }}>
            <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0, opacity: meta.faded ? 0.55 : 1 }} />
            {meta.label}
        </Box>
    );
}

const cellSx = { py: 1.25, px: 2, borderBottom: '1px solid', borderColor: 'divider', verticalAlign: 'top', textAlign: 'left' };
const headSx = {
    py: 1.25, px: 2, textAlign: 'left',
    position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper',
    borderBottom: '1px solid', borderColor: 'divider',
    fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.disabled',
};

export default function UserActivityTimeline({ simEvents, courseEvents, drill }) {
    const events = useMemo(() => buildEvents(simEvents, courseEvents), [simEvents, courseEvents]);

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                        Activity Timeline
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                        Every simulation and course event, newest first
                    </Typography>
                </Box>
                <Typography sx={{ fontSize: 10, color: 'text.disabled', whiteSpace: 'nowrap', ml: 1 }}>
                    {events.length} event{events.length !== 1 ? 's' : ''}
                </Typography>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, maxHeight: 480, overflowY: 'auto' }}>
                {events.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center', color: 'text.disabled', fontSize: 12 }}>
                        No activity for this user.
                    </Box>
                ) : (
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <Box component="thead">
                            <Box component="tr">
                                <Box component="th" sx={{ ...headSx, width: '64px' }}>Date</Box>
                                <Box component="th" sx={headSx}>Status</Box>
                                <Box component="th" sx={headSx}>Name</Box>
                                <Box component="th" sx={headSx}>Detail</Box>
                                <Box component="th" sx={headSx}>Source</Box>
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {events.map((ev, i) => {
                                const meta = EVENT_META[ev.kind];
                                const clickable = ev.entityKind === 'sim';
                                return (
                                    <Box
                                        component="tr"
                                        key={`${ev.date}-${ev.entity}-${i}`}
                                        onClick={() => clickable && drill && drill('sim-detail', ev.entity, { type: 'sim', value: ev.entity })}
                                        sx={{
                                            cursor: clickable ? 'pointer' : 'default',
                                            opacity: meta.faded ? 0.8 : 1,
                                            transition: 'background-color .15s',
                                            '&:hover': clickable ? { bgcolor: 'action.hover' } : undefined,
                                            '&:hover .entity': clickable ? { color: 'secondary.main' } : undefined,
                                        }}
                                    >
                                        <Box component="td" sx={{ ...cellSx, whiteSpace: 'nowrap', fontSize: 11, color: 'text.disabled', fontFeatureSettings: '"tnum"' }}>
                                            {fmtDay(ev.date)}
                                        </Box>
                                        <Box component="td" sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                                            <KindLabel kind={ev.kind} />
                                        </Box>
                                        <Box component="td" sx={cellSx}>
                                            <Typography className="entity" sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', transition: 'color .15s' }}>
                                                {ev.entity}
                                            </Typography>
                                        </Box>
                                        <Box component="td" sx={{ ...cellSx, fontSize: 11, color: 'text.secondary' }}>
                                            {ev.meta || '-'}
                                        </Box>
                                        <Box component="td" sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                                            <SourceBadge source={ev.source} />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>
        </MainCard>
    );
}
